import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { B } from '../constants';
import { flights as flightsApi } from '../api';
import { useCurrency } from '../CurrencyContext';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Checkbox from '../components/ui/Checkbox';
import AirportAutocomplete from '../components/AirportAutocomplete';
import FlightResults from '../components/FlightResults';

export default function FlightSearchScreen({ goScreen, showToast }) {
  const { t } = useTranslation();
  const { currency } = useCurrency();
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [passengers, setPassengers] = useState(1);
  const [cabinClass, setCabinClass] = useState('ECONOMY');
  const [oneWay, setOneWay] = useState(false);
  const [offers, setOffers] = useState(null);
  const [searchId, setSearchId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fallbackLink, setFallbackLink] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!origin?.iataCode || !destination?.iataCode) {
      setError('Pick origin and destination airports');
      return;
    }
    if (!departureDate) {
      setError('Pick a departure date');
      return;
    }
    setLoading(true);
    setError('');
    setOffers(null);
    setFallbackLink(null);
    try {
      const res = await flightsApi.search({
        origin: origin.iataCode,
        destination: destination.iataCode,
        departureDate,
        returnDate: !oneWay && returnDate ? returnDate : undefined,
        passengers,
        cabinClass,
        currency,
      });
      if (res.provider === 'skyscanner_fallback') {
        setFallbackLink(res.bookingLink);
        setError(res.message || 'Search flights on Skyscanner');
      } else {
        setOffers(res.offers || []);
        setSearchId(res.searchId);
      }
    } catch (err) {
      setError(err.message || 'Flight search failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>
      <button
        onClick={() => goScreen('trips')}
        style={{ background: 'none', border: 'none', color: B.muted, cursor: 'pointer', marginBottom: 16, fontSize: 14 }}
      >
        ← {t('nav.myTrips')}
      </button>

      <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, marginBottom: 24, color: B.night }}>
        ✈️ {t('flights.title')}
      </h1>

      <form onSubmit={handleSearch} style={{ background: 'white', borderRadius: 16, padding: 24, marginBottom: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
          <AirportAutocomplete label={t('flights.from')} value={origin} onChange={setOrigin} placeholder="City or IATA code" />
          <AirportAutocomplete label={t('flights.to')} value={destination} onChange={setDestination} placeholder="City or IATA code" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: oneWay ? '1fr 1fr 1fr' : 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>{t('flights.departure')}</label>
            <Input type="date" value={departureDate} onChange={(e) => setDepartureDate(e.target.value)} required style={{ background: '#fafafa' }} />
          </div>
          {!oneWay && (
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>{t('flights.return')}</label>
              <Input type="date" min={departureDate} value={returnDate} onChange={(e) => setReturnDate(e.target.value)} style={{ background: '#fafafa' }} />
            </div>
          )}
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>{t('flights.passengers')}</label>
            <Input type="number" min={1} max={9} value={passengers} onChange={(e) => setPassengers(Math.max(1, Math.min(9, parseInt(e.target.value, 10) || 1)))} style={{ background: '#fafafa' }} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>{t('flights.cabinClass')}</label>
            <Select
              value={cabinClass}
              onChange={(v) => setCabinClass(v)}
              options={[
                { value: 'ECONOMY', label: 'Economy' },
                { value: 'PREMIUM_ECONOMY', label: 'Premium Economy' },
                { value: 'BUSINESS', label: 'Business' },
                { value: 'FIRST', label: 'First' },
              ]}
              size="md"
              variant="filled"
            />
          </div>
        </div>

        <Checkbox
          checked={oneWay}
          onChange={(e) => { setOneWay(e.target.checked); if (e.target.checked) setReturnDate(''); }}
          label={t('flights.oneWay')}
          size="sm"
        />

        {error && !fallbackLink && <div style={{ color: '#dc3545', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <button type="submit" disabled={loading} style={{
          width: '100%', padding: 14, background: loading ? '#9ca3af' : B.forest, color: 'white',
          border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
        }}>
          {loading ? t('flights.searching') : t('flights.searchFlights')}
        </button>
      </form>

      {fallbackLink && (
        <div style={{ textAlign: 'center', padding: 24, background: 'white', borderRadius: 12 }}>
          <p style={{ color: B.muted, marginBottom: 16, fontSize: 14 }}>
            Flight search is not configured. Search directly on Skyscanner.
          </p>
          <a href={fallbackLink} target="_blank" rel="noopener noreferrer" style={{
            display: 'inline-block', padding: '12px 24px', background: B.amber, color: B.night,
            borderRadius: 10, fontSize: 15, fontWeight: 700, textDecoration: 'none',
          }}>
            Search on Skyscanner
          </a>
        </div>
      )}

      {(offers !== null || loading) && (
        <FlightResults offers={offers} loading={loading} searchId={searchId} />
      )}
    </div>
  );
}
